
import { useState, useEffect } from "react";
import moment from 'moment';
import lscache from "lscache";
import styled from "styled-components";
import { NavLink } from 'react-router-dom';

// Api
import {GetLatestVideos} from '../../Api/Video';

// Helpers
import DropDownDialog from '../UI/DropDowns/DropDownDialog';
import PrimaryText from '../UI/Text/PrimaryText';

// Assets
import IconAlertBell from "../../Assets/SVGs/Icons/AlertAndInfo/AlertBell";
import playIcon from '../../Assets/Images/Icons/play-icon.png';

// Models

// Constants
import { Ease } from "../../Constants/EasingCurves";
import { Fonts } from "../../Constants/Fonts";
import { Colours } from "../../Constants/Colours";
import { LocalStorageKeys } from '../../Constants/LocalStorageKeys';
import { RoutePaths } from '../../Constants/RoutePaths';
import { HeaderProperties } from "../../Constants/HeaderProperties";
import {AddToPersistentStorage} from "../../Helpers/CookiesAndLS/LocalStorage";
import {HowManyDifferentInArray} from "../../Helpers/DataTypesGeneral/Arrays";
import VideoDTO from "../../Models/DTOs/VideoDTO";

const HeaderContainer = styled.div`
    color: ${Colours.Text};
    font-size: 28px;
    position:relative;
    display: flex;
    align-content: center;
    flex-wrap: wrap;
    z-index: 3;
`;

const Container = styled.div`
    position: absolute;
    right: 50px;
    top: 0;
    @media screen and (min-width: calc(584em/16)) {
        right: 240px;
    }
`;

const NotificationCount = styled.span<{display?: number}>`
    display: ${p => p.display === 1 ? 'block' : 'none'};
    right: 12px;
    top: 12px;
    text-align: center;
    position: absolute;
    border-radius: 50%;
    width: 7px;
    height: 7px;
    line-height: 20px;
    font-size: 12px;
    color: #ffffff;
    font-weight: 700;
    background: ${Colours.IckonicPink};
    font-family: ${ Fonts.Primary };
    animation-name: pulse;
    animation-duration: 2s;
    animation-iteration-count: infinite;
`;

const ContentContainer = styled.div`
    padding:12px;
    width: 250px;
    box-shadow: rgb(27, 27, 27) 0px 20px 30px -10px;

    > p {
    //text-transform: uppercase;
    color: #f0f0f0;
    width: 100%;
    font-weight: 400;
    font-size: 14pt;
    width: 100%;
    text-align: left;
    //border-bottom: 2px solid ${Colours.IckonicPink};
    padding: 8px 12px;
    margin: 0 0 4px 0;
  }
`;

const PlayIcon = styled.img`
    width: 20px;
    height: 20px;
    padding: 0;
    position: absolute;
    top: calc(50% - 10px);
    right: 12px;
    transform: scale(1);
    opacity: 0.4;
    transition: opacity .45s ${ Ease.Smooth }, transform .45s ${ Ease.Smooth };
`;

const LatestTitle = styled.div`
    display: flex;
    align-items: center;
    margin: 0 0 4px 0;
`;

const LatestContent = styled(NavLink)`
    text-decoration: none;
    position: relative;
    padding: 12px 40px 12px 12px;
    border-radius: 5px;
    display: block;
    margin-bottom: 8px;
    transition: background .15s ${ Ease.Smooth };
    &:last-child {
        margin: 0;
        &:after {
            display: none;
        }
    }


    &:after {
        content: '';
        position: absolute;
        height: 1px;
        width: calc(100% - 24px);
        left: 12px;
        bottom: 0;
        background: rgba(255,255,255,0.065);
    }


    &:hover,
    &:active {
        background: rgba(255,255,255,0.05);
        img {
            transform: scale(1.2);
            //top: calc(50% - 23px);
            opacity: 1;
        }

        & > div {
            //transform: translateX(30px);
        }
    }
`;

const LatestSingleItemWrapper = styled.div`

`;

const DateContainer = styled.span`
    font-family: ${ Fonts.Primary };
    font-size: 9pt;
    color: #bbb;
       box-sizing: border-box;
    display: flex;
    //border-bottom: 1px solid ${Colours.SecondaryHighlight};
`;

const styledIcon = `
  cursor: pointer;
  width: 40px;
      display: flex;
    justify-content: center;
    align-items: center;
  height: ${ HeaderProperties.height };
  box-sizing: border-box;
`;

const AlertIcon = styled.div`
  ${ styledIcon }
  svg {
      padding: 3px;
    * {
      fill: #bcbcbc;
      transition: fill .15s ${ Ease.Smooth };
    }
  }

  &:hover,
  &:active {
      svg {
        * {
          fill: white;
        }
      }
  }
`;

function Notifications() {

    const [isActive, setIsActive] = useState<boolean>(false);
    const [latestFour, setLatestFour] = useState<VideoDTO[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [attempt, setAttempt] = useState(0);
    const [notifyNumber, setNotifyNumber] = useState<number>(0);

    useEffect(() => {
        const controller = new AbortController();
        setLoading(true);
        setError(false);

        async function FetchLatest() {
            try {
                const result = await GetLatestVideos({ take: 4, publishedOnly: true, signal: controller.signal });
                if (controller.signal.aborted) return;
                const items = result.items ?? [];
                const cachedMovies = lscache.get(LocalStorageKeys.LatestMovies);
                const latestFourIds = items.map(item => item.Id);
                setLatestFour(items);
                setNotifyNumber(HowManyDifferentInArray(cachedMovies, latestFourIds));
            } catch {
                if (controller.signal.aborted) return;
                setError(true);
                setNotifyNumber(0);
            } finally {
                if (!controller.signal.aborted) setLoading(false);
            }
        }

        void FetchLatest();
        return () => controller.abort();
    }, [attempt]);

    function OnActiveToggle(value: boolean) {
        setIsActive(value);
    }

    function HeaderClick() {
        if (loading || error) return;
        const ids : number[] = latestFour.map(x => x.Id);
        AddToPersistentStorage(LocalStorageKeys.LatestMovies, ids);
        setNotifyNumber(0);
    };

    return (
        <Container>
            <DropDownDialog
                isActive={isActive}
                setIsActive={OnActiveToggle}
                Header={
                    <>
                        <HeaderContainer onClick={HeaderClick}>
                            <AlertIcon>
                                <IconAlertBell />
                            </AlertIcon>

                            <NotificationCount display={notifyNumber > 0 ? 1 : 0}>{ /**notifyNumber**/ }</NotificationCount>
                        </HeaderContainer>
                    </>
                }>

                <ContentContainer>
                    <PrimaryText>Latest</PrimaryText>
                    {loading && <p role="status">Loading latest videos…</p>}
                    {error && <div role="alert">
                        <p>Latest videos are temporarily unavailable.</p>
                        <button type="button" onClick={() => setAttempt(value => value + 1)}>Retry latest videos</button>
                    </div>}
                        { latestFour.map((content, index) => {
                            return (
                                <LatestContent key={index} onClick={() => {OnActiveToggle(false)}} to={RoutePaths.Watch(content.Id)}>
                                    <PlayIcon src={playIcon}/>
                                    <LatestSingleItemWrapper>
                                        <LatestTitle>
                                            <PrimaryText>{content.Title}</PrimaryText>
                                        </LatestTitle>
                                        <DateContainer>{moment(content.Datetime).format("DD/MM/YYYY").toString()}</DateContainer>
                                    </LatestSingleItemWrapper>
                                </LatestContent>
                            );
                        }) }
                </ContentContainer>

            </DropDownDialog>
        </Container>
    );
}

export default Notifications;
